import { useEffect, useState } from 'react';
import {
    Container, Typography, Box, Alert, Chip, Link, CircularProgress, Button, Dialog,
    DialogTitle, DialogContent, DialogActions, DialogContentText,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { Surface } from '../components/primitives';

const ManageSubscription = () => {
    const [billingHistory, setBillingHistory] = useState({ data: [] });
    const [subscriptionDetails, setSubscriptionDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [canceling, setCanceling] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [jwtToken, setJwtToken] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const session = await fetchAuthSession();
                const token = session.tokens.idToken.toString();
                setJwtToken(token);

                const [historyRes, subRes] = await Promise.all([
                    fetch('https://api.capitolgains.io/user/billing-history', { headers: { Authorization: `Bearer ${token}` } }),
                    fetch('https://api.capitolgains.io/user/subscription-details', { headers: { Authorization: `Bearer ${token}` } }),
                ]);
                if (!historyRes.ok) throw new Error('Failed to fetch billing history');
                if (!subRes.ok) throw new Error('Failed to fetch subscription details');
                const historyData = await historyRes.json();
                const subscriptionData = await subRes.json();
                setBillingHistory(historyData.invoices || { data: [] });
                setSubscriptionDetails(subscriptionData);
            } catch (err) {
                setError('Failed to load subscription data.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleCancel = async () => {
        setCanceling(true);
        setConfirmOpen(false);
        try {
            const response = await fetch('https://api.capitolgains.io/user/cancel-subscription', {
                method: 'POST',
                headers: { Authorization: `Bearer ${jwtToken}` },
            });
            if (!response.ok) throw new Error('Failed to cancel subscription');
            const result = await response.json();
            setSubscriptionDetails(result);
            await fetchAuthSession({ forceRefresh: true });
            navigate('/account');
        } catch (err) {
            setError('Failed to cancel subscription.');
            console.error(err);
        } finally {
            setCanceling(false);
        }
    };

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
            <CircularProgress />
        </Box>
    );

    if (error) return (
        <Container sx={{ pt: 6 }}>
            <Alert severity="error">{error}</Alert>
        </Container>
    );

    // Complimentary access short-circuit: no Stripe subscription, no billing.
    if (subscriptionDetails?.status === 'complimentary') {
        return (
            <Container maxWidth="md" sx={{ pt: 6, pb: 8 }}>
                <Typography variant="h1" sx={{ color: 'text.primary', mb: 0.5 }}>
                    Subscription
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
                    Your account details.
                </Typography>
                <Surface>
                    <Alert severity="info" sx={{ border: 'none', p: 0, bgcolor: 'transparent' }}>
                        You have <strong>complimentary Pro access</strong>. There's no subscription
                        or billing history to display.
                    </Alert>
                </Surface>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ pt: 6, pb: 8 }}>
            <Typography variant="h1" sx={{ color: 'text.primary', mb: 0.5 }}>
                Manage subscription
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
                Review your subscription status and billing history.
            </Typography>

            {subscriptionDetails ? (
                <>
                    {/* Status */}
                    <Surface sx={{ mb: 4 }}>
                        {subscriptionDetails.status === 'canceled' && subscriptionDetails.end_date ? (
                            <Alert severity="info" sx={{ border: 'none', p: 0, bgcolor: 'transparent' }}>
                                Your subscription has been canceled. You still have access until{' '}
                                <strong>{new Date(subscriptionDetails.end_date.replace(' ', 'T')).toLocaleDateString()}</strong>.
                            </Alert>
                        ) : subscriptionDetails.cancel_at ? (
                            <Alert severity="info" sx={{ border: 'none', p: 0, bgcolor: 'transparent' }}>
                                Your subscription is active and scheduled to cancel on{' '}
                                <strong>{new Date(subscriptionDetails.cancel_at * 1000).toLocaleDateString()}</strong>.
                            </Alert>
                        ) : subscriptionDetails.status === 'active' && subscriptionDetails.next_billing_date ? (
                            <Box>
                                <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                    Next billing date
                                </Typography>
                                <Typography variant="h3" sx={{ color: 'text.primary' }}>
                                    {new Date(subscriptionDetails.next_billing_date.replace(' ', 'T')).toLocaleDateString()}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{ mt: 2, color: 'error.light', borderColor: 'rgba(239,68,68,0.3)', '&:hover': { borderColor: 'error.main', bgcolor: 'rgba(239,68,68,0.06)' } }}
                                    onClick={() => setConfirmOpen(true)}
                                    disabled={canceling}
                                >
                                    {canceling ? 'Cancelling…' : 'Cancel subscription'}
                                </Button>
                            </Box>
                        ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Your subscription has been canceled. To resubscribe, see the{' '}
                                <Link component="a" href="/pricing">pricing page</Link>.
                            </Typography>
                        )}
                    </Surface>

                    {/* Billing history */}
                    <Typography variant="h3" sx={{ color: 'text.primary', mb: 2 }}>
                        Billing history
                    </Typography>
                    <Surface padding={0}>
                        {billingHistory.data.length === 0 ? (
                            <Box sx={{ p: 3 }}>
                                <Typography variant="body2" sx={{ color: 'text.muted' }}>No invoices yet.</Typography>
                            </Box>
                        ) : (
                            <Box sx={{ overflow: 'hidden' }}>
                                {/* Header */}
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '2fr 1fr 1fr auto', md: '2.5fr 1fr 1fr 1fr auto' },
                                    alignItems: 'center',
                                    gap: 2,
                                    px: 3, py: 1.5,
                                    bgcolor: 'custom.surface.subtle',
                                    borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                                }}>
                                    <Typography variant="overline" sx={{ color: 'text.muted' }}>Invoice</Typography>
                                    <Typography variant="overline" sx={{ color: 'text.muted', display: { xs: 'none', md: 'block' } }}>Date</Typography>
                                    <Typography variant="overline" sx={{ color: 'text.muted' }}>Status</Typography>
                                    <Typography variant="overline" sx={{ color: 'text.muted' }}>Amount</Typography>
                                    <Typography variant="overline" sx={{ color: 'text.muted' }}>Receipt</Typography>
                                </Box>
                                {billingHistory.data.map((invoice, i) => (
                                    <Box
                                        key={invoice.id}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '2fr 1fr 1fr auto', md: '2.5fr 1fr 1fr 1fr auto' },
                                            alignItems: 'center',
                                            gap: 2,
                                            px: 3, py: 2,
                                            borderBottom: t => i < billingHistory.data.length - 1 ? `1px solid ${t.palette.custom.border.subtle}` : 'none',
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                {invoice.lines.data[0]?.description || 'Pro Subscription'}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.muted', fontFamily: t => t.typography.fontFamilyMono }}>
                                                #{invoice.number}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: { xs: 'none', md: 'block' }, fontFamily: t => t.typography.fontFamilyMono }}>
                                            {new Date(invoice.created * 1000).toLocaleDateString()}
                                        </Typography>
                                        <Chip
                                            label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                            size="small"
                                            sx={{
                                                bgcolor: invoice.status === 'paid' ? 'custom.performance.positiveBg' : 'rgba(255,255,255,0.05)',
                                                color: invoice.status === 'paid' ? 'success.main' : 'text.secondary',
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: t => t.typography.fontFamilyMono, fontWeight: 500 }}>
                                            ${(invoice.amount_paid / 100).toFixed(2)}
                                        </Typography>
                                        <Link
                                            href={invoice.invoice_pdf}
                                            target="_blank"
                                            rel="noopener"
                                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem' }}
                                        >
                                            PDF <OpenInNewIcon sx={{ fontSize: 12 }} />
                                        </Link>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Surface>
                </>
            ) : (
                <Typography variant="body2" sx={{ color: 'text.muted' }}>
                    Subscription details are currently unavailable.
                </Typography>
            )}

            {/* Cancel confirmation dialog */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Cancel subscription?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'text.secondary' }}>
                        You'll retain premium access until the end of your current billing cycle. After that,
                        your account will revert to the free plan. You can resubscribe at any time.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Keep subscription</Button>
                    <Button
                        onClick={handleCancel}
                        sx={{ color: 'error.light', '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}
                    >
                        Cancel subscription
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ManageSubscription;
